import React from "react"
import { Collapsible } from "@base-ui/react/collapsible"
import { useNavigationContext } from "./navigation-context"

// =============================================================================
// Props
// =============================================================================

export type NavigationFooterCalloutProps = {
  /** Promoted content to render (trial indicator, upgrade prompt, etc.) */
  children?: React.ReactNode
}

// =============================================================================
// Styles
// =============================================================================

const panelStyles = [
  "transition-opacity duration-150 ease-out",
  "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0"
].join(" ")

// =============================================================================
// Component
// =============================================================================

/**
 * Fixed-width slot in the navigation footer for promoted content
 * (trial indicator, upgrade prompts, etc.). Sized to the expanded sidebar
 * width so its contents don't reflow during the collapse/expand
 * transition — the nav's `overflow-hidden` clips the right edge while the
 * width animation runs.
 *
 * Fades out when the sidebar collapses and fades back in when it expands,
 * timed to the sidebar width animation.
 */
export function NavigationFooterCallout({ children }: NavigationFooterCalloutProps) {
  const { collapsed } = useNavigationContext()

  return (
    <Collapsible.Root open={!collapsed}>
      <Collapsible.Panel
        data-slot="navigation-footer-callout"
        style={{ width: "var(--navigation-expanded-content-width)" }}
        className={panelStyles}
        keepMounted
      >
        {children}
      </Collapsible.Panel>
    </Collapsible.Root>
  )
}

NavigationFooterCallout.displayName = "Navigation.FooterCallout"
